import { useMutation, useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { type Pagination, toQuery } from "./pagination";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useCandidateLists = (
  pagination: Pagination,
  text: string,
  sort: { column: "createdAt" | "name"; direction: "asc" | "desc" },
) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: [
      "candidate-lists",
      scope.merchantId,
      scope.workspaceId,
      pagination.limit,
      pagination.offset,
      text,
      sort.column,
      sort.direction,
    ],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["candidate-lists"].$get({
            param: scope.param,
            query: {
              ...toQuery(pagination),
              text: text === "" ? undefined : text,
              column: sort.column,
              direction: sort.direction,
            },
          })
        : await client.merchant[":merchantId"]["candidate-lists"].$get({
            param: scope.param,
            query: {
              ...toQuery(pagination),
              text: text === "" ? undefined : text,
              column: sort.column,
              direction: sort.direction,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useCandidateList = (id: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["candidate-lists", scope.merchantId, scope.workspaceId, id],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["candidate-lists"][":id"].$get({
            param: { ...scope.param, id },
          })
        : await client.merchant[":merchantId"]["candidate-lists"][":id"].$get({
            param: { ...scope.param, id },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useCreateCandidateList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: { name: string; description?: string; candidateIds?: string[] }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["candidate-lists"].$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"]["candidate-lists"].$post({
            param: scope.param,
            json: payload,
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidate-lists"] });
    },
  });
};

export const useEditCandidateList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: { id: string; name: string; description?: string }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["candidate-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: payload,
          })
        : await client.merchant[":merchantId"]["candidate-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: payload,
          });
      if ((res.status as number) === 204) return;
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidate-lists"] });
    },
  });
};

export const useDeleteCandidateList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: { id: string }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["candidate-lists"][":id"].$delete({
            param: { ...scope.param, id: payload.id },
          })
        : await client.merchant[":merchantId"]["candidate-lists"][":id"].$delete({
            param: { ...scope.param, id: payload.id },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidate-lists"] });
    },
  });
};

export const useAddToCandidateList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: { id: string; candidates: string[] }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["candidate-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: {
              addCandidates: payload.candidates,
            },
          })
        : await client.merchant[":merchantId"]["candidate-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: {
              addCandidates: payload.candidates,
            },
          });
      if ((res.status as number) === 204) return;
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidate-lists"] });
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
    },
  });
};

export const useRemoveFromCandidateList = () => {
  const scope = useScopedMerchantClient();
  return useMutation({
    mutationFn: async (payload: { id: string; candidates: string[] }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["candidate-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: {
              removeCandidates: payload.candidates,
            },
          })
        : await client.merchant[":merchantId"]["candidate-lists"][":id"].$put({
            param: { ...scope.param, id: payload.id },
            json: {
              removeCandidates: payload.candidates,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["candidate-lists"] });
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
    },
  });
};
